import { AsyncPipe, DOCUMENT, KeyValuePipe, NgClass, NgStyle } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  inject,
  makeStateKey,
  NgZone,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
  QueryList,
  StateKey,
  TemplateRef,
  TransferState,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationStart, Params, Router, RouterLink } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { I18NextModule } from 'angular-i18next';
import { QueryRef } from 'apollo-angular';
import {
  catchError,
  combineLatest,
  map,
  mergeMap,
  NEVER,
  Observable,
  of,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { EllipsisTooltipDirective } from 'src/app/common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { CardContentFragment } from 'src/app/common/fragments/card-content.fragment.generated';
import { CardUserFragment } from 'src/app/common/fragments/card-user.fragment.generated';
import { HeaderSearchService } from 'src/app/_shared/components/header/header-search/header-search.service';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { LoadingComponent } from 'src/app/_shared/components/loading/loading.component';
import { MasonryGridComponent } from 'src/app/_shared/components/masonry-grid/masonry-grid.component';
import { AutocompleteSearchGQL } from 'src/app/_shared/components/prefix-search/prefix-search.query.generated';
import { ReportComponent } from 'src/app/_shared/components/report/report.component';
import { ScrollPositionService } from 'src/app/common/scroll/scroll-position.service';
import { SearchBannerComponent } from 'src/app/pages/search-results/list/search-banner/search-banner.component';
import {
  buildReducedSearchQuery,
  buildSearchQuery,
  DateRange,
  defaultSearchVariables,
  MAT_SNACK_BAR,
  parseArray,
} from 'src/app/common/utils/helpers';
import {
  NearFilter,
  Place,
  PlaceUtility,
  ReportableEntityEnum,
  SearchComparator,
  SearchNode,
  SortDirection,
  SortFilter,
} from 'src/types/graphql';
import { hashCode } from 'utils/hashCode';
import { places, placesShort } from 'utils/places-utility';
import { SearchResultsFilterablesService } from 'utils/search-results-filterable';
import { v4 as uuid } from 'uuid';
import { ContentCardListComponent } from '../../../_shared/components/content-card-list/content-card-list.component';
import { SearchEnginePositionService } from '../search-engine-position.service';
import { SearchResultsFallbackService } from '../search-results-fallback.service';
import {
  AllPlaceUtilitiesGQL,
  AllPlaceUtilitiesQuery,
  AllPlaceUtilitiesQueryVariables,
  GetCoordinatesByAddressGQL,
  ListCardFragment,
  ListSearchGQL,
  ListSearchQuery,
  ListSearchQueryVariables,
} from '../search-results.query.generated';
import { StringReplacePipe } from '../../../_shared/pipes/string-replace.pipe';
import { RouteTranslatePipe } from '../../../_shared/pipes/route-translate.pipe';

@Pipe({
  name: 'carderize',
  standalone: true,
})
export class CarderizePipe implements PipeTransform {
  transform(value: ListCardFragment[]): (CardContentFragment | CardUserFragment)[] {
    return (value || []).map(v => {
      const result = { ...v.result, distance: v.distance };
      return result as CardContentFragment | CardUserFragment;
    });
  }
}

@Component({
  selector: 'sharry-list',
  standalone: true,
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SearchResultsFallbackService],
  imports: [
    NgClass,
    NgStyle,
    AsyncPipe,
    SearchBannerComponent,
    LoadingComponent,
    RouterLink,
    RouteTranslatePipe,
    StringReplacePipe,
    ContentCardListComponent,
    CarderizePipe,
    I18NextModule,
    MatButtonModule,
    IconComponent,
    KeyValuePipe,
    MasonryGridComponent,
    EllipsisTooltipDirective,
    ReportComponent,
  ],
})
export class ListComponent extends BaseComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('fallbackSearchTemplate', { static: true }) fallbackSearchTemplate: TemplateRef<any>;
  bannerSearchTerm: string;
  bannerSearchKind: 'categoryId' | 'keyword';
  cards$: Observable<ListCardFragment[]>;
  cardsInView: ListCardFragment[];
  fallbackCards$: Observable<ListCardFragment[]>;
  busy: boolean = true;
  searchBusy: boolean;
  searchVariables: SearchVariables;
  limit = 12;
  total = 0;
  totalLoaded = 0;
  isFallbackCase: boolean = false;
  fallbackTotal = 0;
  fallbackTotalLoaded = 0;
  snackBarRef: MatSnackBarRef<any>;
  isNearMe = false; // only for banner nearme
  value: string;
  params: Params;
  placeUtilities$: Observable<{ [key: string]: Partial<PlaceUtility>[] }>;
  timeout: NodeJS.Timeout;
  totalStateKey: StateKey<number>;
  utilityIcons = {
    automotive: 'road-horizon',
    culture: 'castle-turret',
    entertainment_and_recreation: 'pinwheel',
    finance: 'bank',
    food_and_drink: 'coffee',
    health_and_wellness: 'first-aid-kit',
    lodging: 'bed',
    places_of_worship: 'church',
    services: 'gear',
    shopping: 'storefront',
    transportation: 'train',
  };

  private cardQuery: QueryRef<ListSearchQuery, ListSearchQueryVariables>;
  private fallbackCardQuery: QueryRef<ListSearchQuery, ListSearchQueryVariables>;
  private placeUtilitiesQuery: QueryRef<AllPlaceUtilitiesQuery, AllPlaceUtilitiesQueryVariables>;
  private uuid = uuid();
  private fromLoadMore = false;
  reportedEntityId: string;
  reportableEntityEnum = ReportableEntityEnum;

  private router = inject(Router);

  @ViewChildren('listItem') listItems!: QueryList<ElementRef>;
  private scrollPositionSubscription: Subscription;
  stopScrollingSub$ = new Subject();
  destroy$ = new Subject();
  hasToScrollToRestorePost = true;
  showNoProProfile: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private searchResultsFallbackService: SearchResultsFallbackService,
    private cd: ChangeDetectorRef,
    private listSearchGQL: ListSearchGQL,
    private getCoordinatesByAddressGQL: GetCoordinatesByAddressGQL,
    @Inject(MAT_SNACK_BAR) private matSnackBar: MatSnackBar,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private autocompleteSearchGQL: AutocompleteSearchGQL,
    private ngZone: NgZone,
    public searchResultsFilterablesService: SearchResultsFilterablesService,
    private searchEnginePositionService: SearchEnginePositionService,
    private transferState: TransferState,
    private allPlaceUtilitiesGQL: AllPlaceUtilitiesGQL,
    public headerSearchService: HeaderSearchService,
    private scrollPositionService: ScrollPositionService,
  ) {
    super();
    this.cardQuery = this.listSearchGQL.watch(
      { limit: 0, skip: 0 },
      {
        initialFetchPolicy: 'cache-only',
        fetchPolicy: 'cache-and-network',
        refetchWritePolicy: 'merge',
      },
    );
    this.fallbackCardQuery = this.listSearchGQL.watch(
      { limit: 0, skip: 0 },
      {
        initialFetchPolicy: 'cache-only',
        fetchPolicy: 'cache-and-network',
        refetchWritePolicy: 'merge',
      },
    );
    this.placeUtilitiesQuery = this.allPlaceUtilitiesGQL.watch(
      { limit: 0 },
      {
        initialFetchPolicy: 'cache-only',
        fetchPolicy: 'cache-and-network',
        useInitialLoading: true,
        refetchWritePolicy: 'overwrite',
      },
    );

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('showNoPro')) this.showNoProProfile = urlParams.get('showNoPro') === 'true';

    this.cards$ = this.cardQuery.valueChanges.pipe(
      mergeMap(d => {
        if (d.loading) {
          this.busy = true;
          cd.detectChanges();
          return NEVER;
        }
        if ((d as any).data?.total) {
          this.total = Number((d as any).data?.total);
          if (this.isPlatformServer()) {
            this.transferState.set(this.totalStateKey, this.total);
          }
        } else if (this.isPlatformBrowser() && this.transferState.get(this.totalStateKey, 0)) {
          this.total = this.transferState.get(this.totalStateKey, 0);
        }
        cd.detectChanges();
        return of(d.data.results);
      }),
      /*      map(data =>
        this.showNoProProfile
          ? data
          : data.filter(item => item.result?.__typename === 'User' && item.result?.isSmallOrganisation === true),
      ),*/
      tap(contents => {
        this.cardsInView = contents;
        this.isFallbackCase = this.cardsInView.length === 0;
        if (this.timeout) {
          clearTimeout(this.timeout);
        }
        this.timeout = setTimeout(() => {
          this.totalLoaded = contents.length;
          cd.detectChanges(); // needed for scrolling with cache elements
          if (
            contents.length &&
            !this.busy &&
            (this.window.scrollY !== this.searchEnginePositionService.getPosition().top || this.fromLoadMore)
          ) {
            this.ngZone.runOutsideAngular(() => {
              this.window.scrollTo({
                ...this.searchEnginePositionService.getPosition(),
                behavior: this.fromLoadMore ? 'auto' : 'smooth',
              });
              if (this.usingCapacitor) {
                this.document.querySelector('.app-wrapper')?.scrollTo({
                  ...this.searchEnginePositionService.getPosition(),
                  behavior: this.fromLoadMore ? 'auto' : 'smooth',
                });
              }
              this.fromLoadMore = false;
            });
          }
        }, 1);
      }),
    );

    this.fallbackCards$ = this.fallbackCardQuery.valueChanges.pipe(
      mergeMap(d => {
        if (d.loading) {
          this.busy = true;
          cd.detectChanges();
          return NEVER;
        }
        if ((d as any).data?.total) {
          this.fallbackTotal = Number((d as any).data?.total);
        }
        cd.detectChanges();

        return of(d.data.results);
      }),
      /*      map(data =>
        this.showNoProProfile
          ? data
          : data.filter(item => item.result?.__typename === 'User' && item.result?.isSmallOrganisation === true),
      ),*/
      tap(contents => {
        this.cardsInView = contents;
        this.fallbackTotalLoaded = contents.length;
        if (this.timeout) {
          clearTimeout(this.timeout);
        }
        this.timeout = setTimeout(() => {
          this.fallbackTotalLoaded = contents.length;
          cd.detectChanges(); // needed for scrolling with cache elements
          if (
            contents.length &&
            !this.busy &&
            (this.window.scrollY !== this.searchEnginePositionService.getPosition().top || this.fromLoadMore)
          ) {
            this.ngZone.runOutsideAngular(() => {
              this.window.scrollTo({
                ...this.searchEnginePositionService.getPosition(),
                behavior: this.fromLoadMore ? 'auto' : 'smooth',
              });
              if (this.usingCapacitor) {
                this.document.querySelector('.app-wrapper')?.scrollTo({
                  ...this.searchEnginePositionService.getPosition(),
                  behavior: this.fromLoadMore ? 'auto' : 'smooth',
                });
              }
              this.fromLoadMore = false;
            });
          }
        }, 1);
      }),
    );
    this.placeUtilities$ = this.placeUtilitiesQuery.valueChanges.pipe(
      map(r => {
        if (r.loading) {
          return {};
        }
        this.reportedEntityId = r.data.allPlaceUtilities[0]?.place?.id;
        return r.data.allPlaceUtilities.reduce<{ [key: string]: Partial<PlaceUtility>[] }>((acc, current) => {
          if (acc[current.family]?.length) {
            acc[current.family].push(current as PlaceUtility);
          } else {
            acc[current.family] = [current as PlaceUtility];
          }
          return acc;
        }, {});
      }),
    );
    this.subscriptions.push(
      combineLatest([this.route.queryParams, this.route.data])
        .pipe(
          tap(([queryParams, routeData]) => {
            let params = { ...(queryParams || {}) };
            if (Object.keys(routeData || {})?.length) {
              const additional = { ...routeData.additional };
              delete routeData.additional;
              params = { ...params, ...(routeData || {}), ...additional };
            }
            this.bannerSearchTerm = params.banner
              ? params.banner
              : parseArray(params.searchCategories).length === 1
                ? parseArray(params.searchCategories)[0]
                : undefined;
            this.totalStateKey = makeStateKey<number>(params.banner ? 'TOTAL_KEY_' + params.banner : 'TOTAL_KEY');
            this.bannerSearchKind = params.banner ? 'keyword' : 'categoryId';
            this.isNearMe = params.banner === 'nearme';
            this.value = params.value;
            this.searchVariables = this.buildFilter(params);
            if (queryParams?.municipality && !queryParams?.banner) {
              this.bannerSearchTerm = queryParams?.municipality;
              this.bannerSearchKind = 'keyword';
            }
            const kindGroup = this.checkIfCustomBanner(params?.banner) ? false : this.checkIfKindGroup(queryParams);
            if (kindGroup && !queryParams?.banner) {
              this.bannerSearchTerm = kindGroup;
              this.bannerSearchKind = 'keyword';
            }
            if (queryParams?.banner) {
              this.bannerSearchTerm = Array.isArray(queryParams?.banner) ? queryParams.banner[0] : queryParams?.banner;
              this.bannerSearchKind = 'keyword';
            }
            if (
              !(
                this.sameParams(params) &&
                (params.signin || params.signup || params.forgotpassword || params.profileemailconfirm)
              )
            ) {
              this.onRefetch(this.sameParams(params));
              this.params = { ...params };
            }
          }),
        )
        .subscribe(),
    );
  }

  ngOnInit(): void {
    this.window.addEventListener('scroll', this.onScroll, true);
    this.scrollPositionSubscription = this.router.events.pipe(takeUntil(this.destroy$)).subscribe(async event => {
      if (event instanceof NavigationStart) {
        const slug = event.url.split('/').at(event.url.split('/').length - 1);

        // let listItems: any = (await lastValueFrom(this.cards$))

        const listItem = this.cardsInView?.find((p: any) => (p.result as any)?.slug === slug);

        if (listItem) {
          this.scrollPositionService.setListItemId(listItem.result.id);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.window.removeEventListener('scroll', this.onScroll, true);
    this.destroy$.next(true);
  }

  onScroll = (): void => {
    // Check if user is near the bottom of the page and not already busy loading more
    if (this.isNearBottom() && !this.searchBusy) {
      if (this.isFallbackCase) {
        this.onFallbackFetchMore();
      } else {
        this.onFetchMore(); // Fetch more cards when the user scrolls near the bottom
      }
    }
  };

  isNearBottom(): boolean {
    const threshold = 300; // Distance from the bottom to trigger loading more (adjust as needed)
    const position = this.window.scrollY + this.window.innerHeight;
    const height = this.document.documentElement.scrollHeight;

    return position > height - threshold;
  }

  ngAfterViewChecked(): void {
    const clickedListItemId = this.scrollPositionService.getListItemId();

    // used to do the scroll to the post only 1 time, cause this lifecycle re-trigger
    if (clickedListItemId && this.hasToScrollToRestorePost) {
      this.hasToScrollToRestorePost = false;
    } else {
      return;
    }

    // const cards = (await firstValueFrom(this.cards$));

    this.listItems.changes.pipe(takeUntil(this.stopScrollingSub$)).subscribe(_ => {
      if (this.listItems.length === this.cardsInView.length) {
        // scroll in view to the post
        setTimeout(() => {
          const element = this.document.getElementById('item-' + clickedListItemId);
          element?.scrollIntoView({ block: 'start', behavior: 'instant' });
        }, 0);

        // reset post saved
        this.scrollPositionService.setListItemId(undefined);
        this.stopScrollingSub$.next(true);
      }
    });
  }

  checkIfCustomBanner(keyword: string): boolean {
    const banners = ['borghi-veneto'];
    return banners.includes(keyword) || keyword in places;
  }

  checkIfKindGroup(_queryParams: Params) {
    _queryParams = JSON.parse(JSON.stringify(_queryParams));
    if (typeof _queryParams?.types === 'string') {
      _queryParams.types = [_queryParams?.types];
    }
    if (typeof _queryParams?.searchQualifications === 'string') {
      _queryParams.searchQualifications = [_queryParams?.searchQualifications];
    }
    const hasTypes = _queryParams?.types?.length >= 1;
    const hasSearchQualifications = _queryParams?.searchQualifications?.length >= 1;
    let keyword;
    if (!(hasTypes && hasSearchQualifications)) {
      if (hasTypes) {
        if (
          _queryParams?.types.every((item: string) =>
            this.searchResultsFilterablesService.wonderFilterableIds.includes(item),
          )
        ) {
          keyword = 'meraviglie';
        }
        if (
          _queryParams?.types.every((item: string) =>
            this.searchResultsFilterablesService.tourFilterableIds.includes(item),
          )
        ) {
          keyword = 'tour';
        }
        if (
          _queryParams?.types.every((item: string) =>
            this.searchResultsFilterablesService.eventFilterableIds.includes(item),
          )
        ) {
          keyword = 'eventi';
        }
        if (
          _queryParams?.types.every((item: string) =>
            this.searchResultsFilterablesService.experienceFilterableIds.includes(item),
          )
        ) {
          keyword = 'esperienze';
        }
        if (
          _queryParams?.types.every((item: string) =>
            this.searchResultsFilterablesService.itemFilterableIds.includes(item),
          )
        ) {
          keyword = 'prodotti';
        }
      }
      if (hasSearchQualifications) {
        if (
          _queryParams?.searchQualifications.every((item: string) =>
            this.searchResultsFilterablesService.userFilterableIds.includes(item),
          )
        ) {
          keyword = 'utenti';
        }
      }
    }
    return keyword;
  }

  sameParams(params: Params): boolean {
    if (!this.params) return true;
    const oldParams: Params = { ...this.params, v: null, signin: null, signup: null };
    const newParams: Params = { ...params, v: null, signin: null, signup: null };
    const newHash = hashCode(JSON.stringify(oldParams));
    const oldHash = hashCode(JSON.stringify(newParams));
    return newHash === oldHash;
  }

  onRefetch(cache = true) {
    if (!cache) {
      this.cardQuery.options.fetchPolicy = 'no-cache';
    } else {
      this.cardQuery.options.fetchPolicy = 'cache-and-network';
    }
    this.cardQuery.options.variables = this.searchVariables;
    this.cardQuery.refetch({ ...this.searchVariables }).then(r => {
      if (!cache) {
        this.onRefetch();
        return;
      }
      if (!r.data.results.length) {
        this.busy = true;
        this.cd.detectChanges();
        this.initNoResultSearch();
      } else {
        this.headerSearchService.setBusy(false);
        this.busy = false;
        this.cd.detectChanges();
        let strippedValue: string;
        if (this.params.value) {
          strippedValue = (this.params.value.startsWith('#') ? this.params.value.substring(1) : this.params.value)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\\/g, '');
        }
        this.initFallback(this.params, strippedValue);
        if (this.params.latitude) {
          this.placeUtilitiesQuery.refetch({
            limit: undefined,
            filter: {
              and: [
                {
                  query: {
                    field: 'placeLatitude',
                    comparator: SearchComparator.Eq,
                    value: Number(this.params.latitude),
                  },
                },
                {
                  query: {
                    field: 'placeLongitude',
                    comparator: SearchComparator.Eq,
                    value: Number(this.params.longitude),
                  },
                },
              ],
            },
          });
        } else {
          this.placeUtilitiesQuery.refetch({ limit: 0 });
        }
      }
    });
  }

  onFallbackRefetch(cache = true, near: NearFilter) {
    if (!cache) {
      this.fallbackCardQuery.options.fetchPolicy = 'no-cache';
    } else {
      this.fallbackCardQuery.options.fetchPolicy = 'cache-and-network';
    }
    this.busy = true;
    this.cd.detectChanges();
    this.fallbackCardQuery.refetch({ ...defaultSearchVariables, near }).then(() => {
      if (!cache) {
        this.onFallbackRefetch(true, near);
        return;
      }
      this.busy = false;
      this.headerSearchService.setBusy(false);
      this.cd.detectChanges();
      let strippedValue: string;
      if (this.params.value) {
        strippedValue = (this.params.value.startsWith('#') ? this.params.value.substring(1) : this.params.value)
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\\/g, '');
      }
      this.initFallback(this.params, strippedValue);
    });
  }

  onFetchMore() {
    if (this.totalLoaded === this.total) {
      return;
    }
    this.searchEnginePositionService.setPosition({ top: this.window.scrollY, left: this.window.scrollX });
    this.searchBusy = true;
    this.fromLoadMore = true;
    this.cd.detectChanges();
    this.cardQuery
      .fetchMore({
        variables: {
          ...this.searchVariables,
          skip: this.totalLoaded,
        },
      })
      .then(() => {
        this.searchBusy = false;
        this.cd.detectChanges();
      });
  }

  onFallbackFetchMore() {
    if (this.fallbackTotalLoaded === this.fallbackTotal) {
      return;
    }
    this.searchEnginePositionService.setPosition({ top: this.window.scrollY, left: this.window.scrollX });
    this.searchBusy = true;
    this.fromLoadMore = true;
    this.cd.detectChanges();
    this.fallbackCardQuery
      .fetchMore({
        variables: {
          ...defaultSearchVariables,
          skip: this.fallbackTotalLoaded,
        },
      })
      .then(() => {
        this.searchBusy = false;
        this.cd.detectChanges();
      });
  }

  buildFilter(params: Params) {
    const searchQualifications = parseArray(params.searchQualifications);
    const withUsers = !!searchQualifications.length || params.withUsers === 'true';
    let strippedValue: string;
    if (params.value) {
      strippedValue = (params.value.startsWith('#') ? params.value.substring(1) : params.value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\\/g, '');
    }
    const searchVariables = this.buildSearchVariablesFilters(params, withUsers);
    if (params.region || params.province) {
      //strippedValue = trova
      let values = (params.region || params.province).replace(', Italia', '');
      Object.keys(placesShort).forEach(shortProvince => {
        values = values.replace(`, ${shortProvince}`, '');
      });
      searchVariables.filter.and.push({ or: buildReducedSearchQuery(values) });
      searchVariables.reorder = true;
    } else if (!isNaN(Number(params.latitude)) && !isNaN(Number(params.longitude))) {
      searchVariables.near = {
        coordinates: {
          latitude: Number(params.latitude),
          longitude: Number(params.longitude),
        },
        maxDistance: params.distance ? Number(params.distance) : 100,
      };
      searchVariables.sort = {
        key: 'distance',
        direction: SortDirection.Asc,
        nullsLast: true,
      };
    }
    if (params.beginDate) {
      // APPLY next order only when sharry and event types
      searchVariables.nextDateOrder = true;
      searchVariables.count = null; // workaround for distinct
    }
    switch (params.prefix) {
      case 'location':
        if (!params.region && !params.province && !searchVariables.near) {
          searchVariables.near = {
            coordinates: {
              latitude: 45.6509,
              longitude: 11.7852,
            },
            maxDistance: params.distance ? Number(params.distance) : 100,
          };
          searchVariables.sort = {
            key: 'distance',
            direction: SortDirection.Asc,
            nullsLast: true,
          };
        }
        break;
      case 'title':
        if (strippedValue) {
          if (params.value.startsWith('#')) {
            const values = strippedValue.split(' ');
            values.forEach(value => {
              searchVariables.filter.and.push({
                or: [
                  {
                    query: {
                      field: 'user_hashtag',
                      comparator: SearchComparator.ContWord,
                      value,
                    },
                  },
                  {
                    query: {
                      field: 'sharry_content_hashtag',
                      comparator: SearchComparator.ContWord,
                      value,
                    },
                  },
                ],
              });
            });
          } else {
            const values = strippedValue.split(',')[0].split(' ');
            values.forEach(value => {
              searchVariables.filter.and.push({ or: buildSearchQuery(value) });
              searchVariables.titleFirst = strippedValue;
            });
            searchVariables.withNoDates = !params.beginDate;
          }
        } else {
          searchVariables.reorder = true;
        }
        searchVariables.filter.and[0].or[0].and.push(
          {
            query: {
              field: 'sharry_content_region',
              comparator: SearchComparator.In,
              values: parseArray(params.regions),
            },
          },
          {
            query: {
              field: 'sharry_content_requires_payment',
              comparator: SearchComparator.Eq,
              value: params.freeOnly === 'true' ? false : null,
            },
          },
          {
            query: {
              field: 'sharry_content_owner_gold_subscription_status',
              comparator: SearchComparator.In,
              value: params.goldOnly === 'true' ? [0, 2] : null,
            },
          },
          {
            query: {
              field: 'sharry_content_reduced_mobility',
              comparator: SearchComparator.Eq,
              value: params.reducedMobility === 'true' ? true : null,
            },
          },
          {
            query: {
              field: 'sharry_content_animals_allowed',
              comparator: SearchComparator.Eq,
              value: params.animalsAllowed === 'true' ? true : null,
            },
          },
          {
            query: {
              field: 'sharry_content_sharry_selection',
              comparator: SearchComparator.In,
              values: parseArray(params.sharrySelections),
            },
          },
        );
        break;
    }
    if (params.soldBySharryland) {
      searchVariables.soldBySharryland = params.soldBySharryland === 'true';
    }
    return searchVariables;
  }

  getContentTypeFormFilterable(types: string[], withUsers = false) {
    const values = withUsers ? ['users'] : [];
    if (this.searchResultsFilterablesService.filterables.sharry.find(f => types.includes(f.id))) {
      values.push('sharry');
    }
    if (this.searchResultsFilterablesService.filterables.wonder.find(f => types.includes(f.id))) {
      values.push('wonder');
    }
    if (this.searchResultsFilterablesService.filterables.event.find(f => types.includes(f.id))) {
      values.push('event');
    }
    if (!types.length && !withUsers) values.push('wonder', 'sharry', 'event');
    return values;
  }

  buildSearchVariablesFilters(params: Params, withUsers: boolean = false): SearchVariables {
    let dateRange: DateRange<Date>;
    if (params.beginDate) {
      dateRange = new DateRange(new Date(params.beginDate), new Date(params.endDate));
    }
    const types = params.types ? parseArray(params.types) : [];
    const searchQualifications = parseArray(params.searchQualifications);
    return {
      filter: {
        and: [
          {
            or: [
              {
                and: [
                  {
                    query: {
                      field: 'sharry_content_content_type',
                      comparator: SearchComparator.In,
                      values: this.getContentTypeFormFilterable(types, withUsers),
                    },
                  },
                  {
                    query: {
                      field: 'sharry_content_sharry_kind_id',
                      comparator: SearchComparator.In,
                      values: types,
                    },
                  },
                  {
                    query: {
                      field: 'sharry_content_category_sharry_contents_category_id',
                      comparator: SearchComparator.In,
                      values: parseArray(params.searchCategories),
                    },
                  },
                  {
                    query: {
                      field: 'sharry_content_accessibility_category_sharry_contents_accessibility_category_id',
                      comparator: SearchComparator.In,
                      values: parseArray(params.searchAccessibilityCategories),
                    },
                  },
                  {
                    query: {
                      field: 'sharry_content_id',
                      comparator: SearchComparator.NotIn,
                      values: parseArray(params.relatedIds),
                    },
                  },
                  {
                    or: [
                      {
                        query: {
                          field: 'sharry_content_content_type',
                          comparator: SearchComparator.NotIn,
                          values: dateRange ? ['user', 'wonder'] : undefined,
                        },
                      },
                      {
                        and: [
                          {
                            query: {
                              field: 'sharry_content_begins_at',
                              comparator: SearchComparator.Datelteq,
                              value: dateRange ? dateRange.start : null,
                            },
                          },
                          {
                            query: {
                              field: 'sharry_content_ends_at',
                              comparator: SearchComparator.Dategteq,
                              value: dateRange ? dateRange.end : null,
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                and: [
                  {
                    query: {
                      field: withUsers && !dateRange ? 'user_id' : '',
                      comparator: SearchComparator.IsNotNull,
                    },
                  },
                  {
                    query: {
                      field: withUsers && !dateRange ? 'user_categories_id' : '',
                      comparator: SearchComparator.In,
                      values: withUsers && !dateRange ? parseArray(params.searchCategories) : [],
                    },
                  },
                  {
                    query: {
                      field: withUsers && !dateRange ? 'user_qualification_id' : '',
                      comparator: SearchComparator.In,
                      values: withUsers && !dateRange ? searchQualifications : [],
                    },
                  },
                ],
              },
              {
                and: [
                  {
                    query: {
                      field: !dateRange ? 'featured_card_id' : '',
                      comparator: SearchComparator.IsNotNull,
                    },
                  },
                  {
                    query: {
                      field: !dateRange ? 'featured_card_sharry_kind_id' : '',
                      comparator: SearchComparator.In,
                      values:
                        this.getContentTypeFormFilterable(types, withUsers).includes('wonder') ||
                        this.getContentTypeFormFilterable(types, withUsers).includes('sharry') ||
                        this.getContentTypeFormFilterable(types, withUsers).includes('event')
                          ? types
                          : ['users'],
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      count: true,
      limit: this.limit,
      skip: 0,
    };
  }

  initFallback(params: Params, strippedValue: string) {
    if (strippedValue && params.prefix === 'title' && !params.value.startsWith('#')) {
      this.subscriptions.push(
        this.autocompleteSearchGQL
          .fetch(
            {
              text: strippedValue
                .replace(/[\u2018\u2019]/g, "'")
                .replace('#', '')
                .replace('«', '')
                .replace('»', '')
                .replace(/\\/g, ''),
              sessionId: this.uuid,
              longitude: '',
              latitude: '',
              limit: 5,
            },
            {
              fetchPolicy: 'network-only',
            },
          )
          .pipe(
            map(r => r.data.results.find(r => r.__typename === 'Place') as unknown as Place & { placeName: string }),
            mergeMap(place => {
              if (!place || params.ignoreLocation) return NEVER;
              return this.getCoordinatesByAddressGQL
                .fetch({
                  address: place.placeName
                    .replace(/[\u2018\u2019]/g, "'")
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/\\/g, ''),
                })
                .pipe(
                  map(r => ({
                    ...place,
                    latitude: r.data.coordinatesByAddress.latitude,
                    longitude: r.data.coordinatesByAddress.longitude,
                  })),
                );
            }),
          )
          .subscribe({
            next: place => {
              if (place) {
                const fallbackParams: Params = {
                  ...this.params,
                  searchQualifications: null,
                  prefix: 'location',
                  distance: 50,
                  value: place.placeName,
                  latitude: place.latitude,
                  longitude: place.longitude,
                  ignoreLocation: true,
                };
                this.snackBarRef = this.matSnackBar.openFromTemplate(this.fallbackSearchTemplate, {
                  data: {
                    fallbackParams,
                    place,
                  },
                  duration: 5000,
                  panelClass: 'search-results__fallback-search__panel',
                });
              }
              this.cd.detectChanges();
            },
          }),
      );
    }
  }

  initNoResultSearch() {
    this.subscriptions.push(
      this.searchResultsFallbackService
        .getFallbackLocation()
        .pipe(
          catchError(() => {
            const rome = {
              latitude: 41.902782,
              longitude: 12.496366,
            };
            return of(rome);
          }),
          tap(location => {
            const near = {
              coordinates: {
                latitude:
                  !!location.latitude && !isNaN(Number(location.latitude)) ? Number(location.latitude) : 45.6509,
                longitude:
                  !!location.longitude && !isNaN(Number(location.longitude)) ? Number(location.longitude) : 11.7852,
              },
              maxDistance: 300,
            };
            this.onFallbackRefetch(false, near);
          }),
        )
        .subscribe(),
    );
  }
}

interface SearchVariables {
  filter?: SearchNode;
  sort?: SortFilter;
  count?: boolean;
  skip?: number;
  limit?: number;
  near?: NearFilter;
  titleFirst?: string;
  withNoDates?: boolean;
  nextDateOrder?: boolean;
  reorder?: boolean;
  soldBySharryland?: boolean;
}
